'use client';

/**
 * RecordTimeline Component
 *
 * Displays a timeline of record activity/events with support for
 * chronological ordering, field changes, and pagination.
 */

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useMemo } from 'react';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles, neonAccents } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import type { TimelineEvent } from '../../types/entityFramework';
import { TimelineItem } from './TimelineItem';

// ============================================================================
// Types
// ============================================================================

export interface RecordTimelineProps {
  /** Array of timeline events */
  events: TimelineEvent[];
  /** Loading state */
  loading?: boolean;
  /** Whether more events are available */
  hasMore?: boolean;
  /** Callback to load more events */
  onLoadMore?: () => void;
  /** Sort order for events */
  order?: 'asc' | 'desc';
  /** Maximum number of events to display */
  maxEvents?: number;
  /** Whether to show field changes */
  showFieldChanges?: boolean;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Display variant */
  variant?: 'compact' | 'detailed';
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(() => ({
  width: '100%',
}));

const StyledLoadMoreButton = styled(motion.button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '12px 20px',
    marginTop: 16,
    marginLeft: 52, // Align with content (icon width 36 + margin 16)
    maxWidth: 'calc(100% - 52px)',
    borderRadius: 12,
    border: `1px solid ${neonStyles ? neonStyles.border : glass.border}`,
    background: neonStyles ? neonStyles.background : glass.background,
    color: neonStyles ? neonAccents[$neonAccent!].base : theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: transitionPresets.button,
    fontFamily: theme.typography.fontFamily,

    '&:hover': {
      background: neonStyles ? `${neonStyles.background}` : glass.backgroundHover,
      border: `1px solid ${neonStyles ? neonStyles.border : glass.borderHover}`,
      color: neonStyles ? neonAccents[$neonAccent!].base : theme.palette.text.primary,
    },

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
});

const StyledEmptyState = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 16,
    background: glass.background,
    border: `1px solid ${glass.border}`,
    textAlign: 'center',
  };
});

const StyledLoadingOverlay = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 40,
  gap: 16,
});

// ============================================================================
// Component
// ============================================================================

export function RecordTimeline({
  events,
  loading = false,
  hasMore = false,
  onLoadMore,
  order = 'desc',
  maxEvents,
  showFieldChanges = true,
  neonAccent,
  variant = 'compact',
  className,
}: RecordTimelineProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Sort and limit events
  const sortedEvents = useMemo(() => {
    const sorted = [...events].sort((a, b) => {
      const aTime = new Date(a.timestamp).getTime();
      const bTime = new Date(b.timestamp).getTime();
      return order === 'desc' ? bTime - aTime : aTime - bTime;
    });

    if (maxEvents && maxEvents > 0) {
      return sorted.slice(0, maxEvents);
    }

    return sorted;
  }, [events, order, maxEvents]);

  const handleLoadMore = useCallback(() => {
    if (onLoadMore && !loading) {
      onLoadMore();
    }
  }, [onLoadMore, loading]);

  // Empty state
  if (!loading && sortedEvents.length === 0) {
    return (
      <StyledContainer $isDark={isDark} $neonAccent={neonAccent} className={className}>
        <StyledEmptyState $isDark={isDark}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            stroke={isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}
            strokeWidth="2"
          >
            <circle cx="24" cy="24" r="20" />
            <path d="M24 14v10l7 7" />
          </svg>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mt: 2,
            }}
          >
            No activity yet
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
            }}
          >
            Events will appear here as they occur
          </Typography>
        </StyledEmptyState>
      </StyledContainer>
    );
  }

  // Initial loading state
  if (loading && sortedEvents.length === 0) {
    return (
      <StyledContainer $isDark={isDark} $neonAccent={neonAccent} className={className}>
        <StyledLoadingOverlay>
          <CircularProgress
            size={32}
            sx={{
              color: neonAccent ? neonAccents[neonAccent].base : 'primary.main',
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Loading activity...
          </Typography>
        </StyledLoadingOverlay>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer $isDark={isDark} $neonAccent={neonAccent} className={className}>
      {/* Timeline Events */}
      <AnimatePresence mode="popLayout">
        {sortedEvents.map((event, index) => (
          <TimelineItem
            key={event.id}
            event={event}
            showFieldChanges={showFieldChanges}
            neonAccent={neonAccent}
            variant={variant}
            showConnector={true}
            isLast={index === sortedEvents.length - 1 && !hasMore}
          />
        ))}
      </AnimatePresence>

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <StyledLoadMoreButton
          $isDark={isDark}
          $neonAccent={neonAccent}
          onClick={handleLoadMore}
          disabled={loading}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          {loading ? (
            <>
              <CircularProgress
                size={16}
                sx={{
                  color: neonAccent ? neonAccents[neonAccent].base : 'inherit',
                }}
              />
              Loading...
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M8 3v10M4 9l4 4 4-4" />
              </svg>
              Load more events
            </>
          )}
        </StyledLoadMoreButton>
      )}

      {/* Loading indicator when paginating */}
      {loading && sortedEvents.length > 0 && !hasMore && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 3,
            ml: '52px',
          }}
        >
          <CircularProgress
            size={24}
            sx={{
              color: neonAccent ? neonAccents[neonAccent].base : 'primary.main',
            }}
          />
        </Box>
      )}
    </StyledContainer>
  );
}

RecordTimeline.displayName = 'RecordTimeline';

export default RecordTimeline;
